import React, { useState, useEffect } from 'react';
import { getUsers, addUser as apiAddUser, updateUser as apiUpdateUser, deleteUser as apiDeleteUser } from '@recipe/shared';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, Users, Shield, ShieldCheck, Check, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './Admin.css';

const Admin = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    try {
      const list = await getUsers();
      setUsers(list);
    } catch (error) {
      toast.error('获取用户列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) {
      toast.error('请输入邮箱地址');
      return;
    }

    try {
      await apiAddUser({
        email: newEmail.trim(),
        displayName: newName.trim(),
        isAllowed: true,
      });

      toast.success(`用户 ${newEmail} 已添加`);
      setNewEmail('');
      setNewName('');
      fetchUsers();
    } catch (error) {
      toast.error('添加用户失败: ' + error.message);
    }
  };

  const handleToggleAllowed = async (targetUser) => {
    try {
      const nextAllowed = !targetUser.isAllowed;
      await apiUpdateUser(targetUser.id, { isAllowed: nextAllowed });
      toast.success(nextAllowed ? '已恢复该用户访问权限' : '已禁用该用户');
      fetchUsers();
    } catch (error) {
      toast.error('更新权限失败: ' + error.message);
    }
  };

  const handleToggleAdmin = async (targetUser) => {
    if (targetUser.id === user?.id) {
      toast.error('不能取消自己的管理员权限');
      return;
    }
    try {
      const nextAdmin = !targetUser.isAdmin;
      await apiUpdateUser(targetUser.id, { isAdmin: nextAdmin });
      toast.success(nextAdmin ? '已设为管理员' : '已取消管理员');
      fetchUsers();
    } catch (error) {
      toast.error('更新管理员权限失败: ' + error.message);
    }
  };

  const handleDeleteUser = async (targetUser) => {
    if (targetUser.id === user?.id) {
      toast.error('不能删除自己的账户');
      return;
    }

    if (window.confirm(`确定要删除用户 ${targetUser.email} 吗？`)) {
      try {
        await apiDeleteUser(targetUser.id);
        toast.success(`用户 ${targetUser.email} 已删除`);
        fetchUsers();
      } catch (error) {
        toast.error('删除用户失败: ' + error.message);
      }
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading">正在加载用户列表...</div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div className="header-content">
          <div>
            <h1><Users size={24} /> 用户与权限管理</h1>
            <p>基于 Cloudflare D1 数据库管理可访问本应用的用户白名单</p>
          </div>
        </div>
      </div>

      <div className="admin-content">
        {/* Add User Form */}
        <div className="add-user-section">
          <h2>添加白名单用户</h2>
          <form onSubmit={handleAddUser} className="add-user-form">
            <div className="form-group">
              <label>邮箱地址 *</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="user@example.com"
                required
              />
            </div>
            <div className="form-group">
              <label>显示名称</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="用户姓名"
              />
            </div>
            <button type="submit" className="add-btn">
              <Plus size={16} />
              添加用户
            </button>
          </form>
        </div>

        {/* Users List */}
        <div className="users-section">
          <h2>已授权用户 ({users.length})</h2>
          {users.length === 0 ? (
            <div className="no-users">
              <p>暂无用户</p>
            </div>
          ) : (
            <div className="users-list">
              {users.map((u) => (
                <div key={u.id} className="user-item">
                  <div className="user-info">
                    <div className="user-email" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {u.email}
                      {u.isAdmin && (
                        <span style={{ fontSize: '11px', background: '#37352f', color: '#fff', padding: '2px 6px', borderRadius: '4px' }}>
                          管理员
                        </span>
                      )}
                      {!u.isAllowed && (
                        <span style={{ fontSize: '11px', background: '#e03e3e', color: '#fff', padding: '2px 6px', borderRadius: '4px' }}>
                          已禁用
                        </span>
                      )}
                    </div>
                    {u.displayName && (
                      <div className="user-name">{u.displayName}</div>
                    )}
                    <div className="user-date">
                      注册时间: {u.createdAt || '未知'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => handleToggleAdmin(u)}
                      className="edit-btn"
                      title={u.isAdmin ? '取消管理员' : '设为管理员'}
                      style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #e9e9e7', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}
                    >
                      {u.isAdmin ? '降级' : '设为管理员'}
                    </button>
                    <button
                      onClick={() => handleToggleAllowed(u)}
                      className="edit-btn"
                      title={u.isAllowed ? '禁用账号' : '启用账号'}
                      style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #e9e9e7', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}
                    >
                      {u.isAllowed ? '禁用' : '启用'}
                    </button>
                    <button
                      onClick={() => handleDeleteUser(u)}
                      className="remove-btn"
                      title="删除用户"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
